import React, { useState, useEffect, useRef } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import CopyLinkButton from './CopyLinkButton';
import { 
  ArrowLeft, Briefcase, MapPin, Building2, Clock, IndianRupee, Users,
  Edit, Plus, Calendar, User, Eye, Loader2, AlertCircle, GripVertical,
  ChevronRight, Upload, CheckCircle, FileText, X, Search, ExternalLink, Tag
} from 'lucide-react';

const STATUS_COLORS = {
  'Open': 'bg-green-100 text-green-700',
  'Closed': 'bg-red-100 text-red-700',
  'On Hold': 'bg-amber-100 text-amber-700'
};

const candidateStatusColors = {
  'New': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Offer Extended': 'bg-purple-100 text-purple-700',
  'Offer Accepted': 'bg-green-100 text-green-700',
  'Joined': 'bg-emerald-100 text-emerald-700'
};

const JobDetail = ({ jobId, onBack, onViewCandidate, initialTab = 'overview', onTabChange }) => {
  const {
    fetchJobs, updateJob, jobs,
    fetchCandidates, fetchAllCandidates, candidates, createCandidate, updateCandidate,
    fetchInterviews, pipelineTemplates, fetchPipelineTemplates,
    updateCandidateStage, parseResume, fetchTags
  } = useRecruitment();

  const [job, setJob] = useState(null);
  const [jobCandidates, setJobCandidates] = useState([]);
  const [jobInterviews, setJobInterviews] = useState([]);
  const [allCandidatesForSearch, setAllCandidatesForSearch] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  // The portal keeps this tab in the URL (?job_tab=), so coming back from a
  // candidate opened here lands on the same tab.
  const [activeTab, setActiveTab] = useState(initialTab);
  const changeTab = (tab) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  // Edit job dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Add candidate dialog
  const [showAddCandidateDialog, setShowAddCandidateDialog] = useState(false);
  const [addCandidateMode, setAddCandidateMode] = useState('new'); // 'new' or 'existing'
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    current_company: '',
    years_of_experience: '',
    skills: [],
    current_ctc: '',
    expected_ctc: '',
    notice_period: '',
    source: 'Direct',
    referred_by: '',
    tags: [],
    notes: '',
    resume_file_id: '',
    resume_filename: ''
  });
  const [parsedData, setParsedData] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const resumeInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);

  // Bulk upload
  const [bulkResults, setBulkResults] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Tag autocomplete
  const [allTags, setAllTags] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Drag and drop
  const [draggedCandidate, setDraggedCandidate] = useState(null);

  useEffect(() => {
    loadJobData();
  }, [jobId]);

  // Load the "Link existing" search list only when someone is actually about
  // to search it: the add dialog is open AND in 'existing' mode. Adding a NEW
  // candidate never needs it. Re-fetched on each open so it reflects
  // candidates added elsewhere since.
  //
  // `silent` matters: fetchAllCandidates otherwise overwrites the shared
  // context `candidates` with EVERY candidate. loadJobData leaves that list
  // holding this job's candidates; a non-silent call here would swap it out
  // from under anything reading it.
  const [loadingSearchCandidates, setLoadingSearchCandidates] = useState(false);
  useEffect(() => {
    if (!showAddCandidateDialog || addCandidateMode !== 'existing') return undefined;
    let cancelled = false;
    setLoadingSearchCandidates(true);
    fetchAllCandidates({}, { silent: true })
      .then((rows) => { if (!cancelled) setAllCandidatesForSearch(rows || []); })
      .finally(() => { if (!cancelled) setLoadingSearchCandidates(false); });
    return () => { cancelled = true; };
    // fetchAllCandidates is a stable useCallback from context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddCandidateDialog, addCandidateMode]);

  const loadJobData = async () => {
    setLoading(true);
    try {
      await fetchJobs();
      await fetchPipelineTemplates();
      
      const currentJob = jobs.find(j => j.id === jobId);
      if (currentJob) {
        setJob(currentJob);
        setEditForm({
          title: currentJob.title || '',
          department: currentJob.department || '',
          location: currentJob.location || '',
          employment_type: currentJob.employment_type || 'Full-time',
          experience_min: currentJob.experience_min?.toString() || '',
          experience_max: currentJob.experience_max?.toString() || '',
          salary_min: currentJob.salary_min?.toString() || '',
          salary_max: currentJob.salary_max?.toString() || '',
          description: currentJob.description || '',
          requirements: currentJob.requirements || '',
          status: currentJob.status || 'Open'
        });

        // Get pipeline stages
        if (currentJob.pipeline_template_id) {
          const template = pipelineTemplates.find(t => t.id === currentJob.pipeline_template_id);
          setStages(template?.stages || ['Screening', 'Interview', 'Offer', 'Hired']);
        } else {
          const defaultTemplate = pipelineTemplates.find(t => t.is_default);
          setStages(defaultTemplate?.stages || ['Screening', 'Interview', 'Offer', 'Hired']);
        }
      }

      // The "Link existing" search list is NOT loaded here any more — see the
      // effect on showAddCandidateDialog/addCandidateMode above. loadJobData
      // runs on open AND after every add, link and drag on this page, and each
      // run used to page through the ENTIRE candidate collection, 200 at a
      // time in sequence, for a list only one dialog reads.

      // Candidates for this specific job (paginated to bypass the 200-cap)
      const jobCandidatesData = await fetchAllCandidates({ job_id: jobId });
      setJobCandidates(jobCandidatesData || []);

      // Fetch interviews for this job
      const interviewsData = await fetchInterviews({ job_id: jobId });
      setJobInterviews(interviewsData || []);

      // Fetch all tags for autocomplete
      const tagsData = await fetchTags();
      setAllTags(tagsData || []);

    } catch (error) {
      console.error('Error loading job:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  // Reload job data when jobs array changes
  useEffect(() => {
    if (jobs.length > 0 && jobId) {
      const currentJob = jobs.find(j => j.id === jobId);
      if (currentJob) {
        setJob(currentJob);
      }
    }
  }, [jobs, jobId]);

  const handleUpdateJob = async () => {
    setFormLoading(true);
    const result = await updateJob(jobId, {
      ...editForm,
      experience_min: editForm.experience_min ? parseInt(editForm.experience_min) : null,
      experience_max: editForm.experience_max ? parseInt(editForm.experience_max) : null,
      salary_min: editForm.salary_min ? parseFloat(editForm.salary_min) : null,
      salary_max: editForm.salary_max ? parseFloat(editForm.salary_max) : null
    });
    setFormLoading(false);

    if (result.success) {
      toast.success('Job updated');
      setShowEditDialog(false);
      loadJobData();
    } else {
      toast.error(result.error);
    }
  };

  const handleLinkExistingCandidate = async (candidateId) => {
    setFormLoading(true);
    const result = await updateCandidate(candidateId, { job_id: jobId });
    setFormLoading(false);

    if (result.success) {
      toast.success('Candidate linked to this job');
      closeAddCandidateDialog();
      loadJobData();
    } else {
      toast.error(result.error);
    }
  };

  const handleAddCandidate = async () => {
    // Handle adding new candidate
    if (!candidateForm.name || !candidateForm.email) {
      toast.error('Name and email are required');
      return;
    }

    setFormLoading(true);
    const result = await createCandidate({
      ...candidateForm,
      job_id: jobId,
      years_of_experience: candidateForm.years_of_experience ? parseFloat(candidateForm.years_of_experience) : null,
      current_ctc: candidateForm.current_ctc ? parseFloat(candidateForm.current_ctc) : null,
      expected_ctc: candidateForm.expected_ctc ? parseFloat(candidateForm.expected_ctc) : null,
      resume_file_id: candidateForm.resume_file_id || null,
      resume_filename: candidateForm.resume_filename || null,
      location: candidateForm.location || null
    });
    setFormLoading(false);

    if (result.success) {
      toast.success('Candidate added');
      closeAddCandidateDialog();
      loadJobData();
    } else {
      toast.error(result.error);
    }
  };

  const closeAddCandidateDialog = () => {
    setShowAddCandidateDialog(false);
    setAddCandidateMode('new');
    setCandidateSearchQuery('');
    setCandidateForm({
      name: '', email: '', phone: '', location: '', current_company: '', years_of_experience: '',
      skills: [], current_ctc: '', expected_ctc: '', notice_period: '',
      source: 'Direct', referred_by: '', tags: [], notes: '',
      resume_file_id: '', resume_filename: ''
    });
    setParsedData(null);
    setSkillInput('');
    setTagInput('');
  };

  const addSkill = () => {
    if (skillInput.trim() && !candidateForm.skills.includes(skillInput.trim())) {
      setCandidateForm(prev => ({ ...prev, skills: [...prev.skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setCandidateForm(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addTag = (tagValue) => {
    const tag = (tagValue || tagInput).trim();
    if (tag && !candidateForm.tags.includes(tag)) {
      setCandidateForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      if (!allTags.includes(tag)) {
        setAllTags(prev => [...prev, tag].sort());
      }
    }
    setTagInput('');
    setShowTagSuggestions(false);
  };

  const removeTag = (tag) => {
    setCandidateForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const filteredTagSuggestions = tagInput.trim().length > 0
    ? allTags.filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !candidateForm.tags.includes(t))
    : [];

  const openCandidateNewWindow = (candidateId) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('tab', 'candidates');
    currentUrl.searchParams.set('candidate_id', candidateId);
    window.open(currentUrl.toString(), '_blank');
  };

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const initialResults = files.map(f => ({
      filename: f.name,
      status: 'pending',
      parsed_data: null,
      candidate_id: null,
      error: null
    }));
    setBulkResults(initialResults);
    setUploadDialogOpen(true);
    setBulkProcessing(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      setBulkResults(prev => prev.map((r, idx) =>
        idx === i ? { ...r, status: 'uploading' } : r
      ));

      try {
        const result = await parseResume(file, jobId);

        if (!result.success) {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'error', error: result.error || 'Failed to parse' } : r
          ));
          continue;
        }

        const parsed = result.data?.parsed_data || {};
        const resumeFileId = result.data?.resume_file_id || null;
        const resumeFilename = result.data?.resume_filename || file.name;

        if (parsed.error) {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'error', parsed_data: parsed, error: parsed.error } : r
          ));
          continue;
        }

        if (result.data?.duplicate) {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'duplicate', parsed_data: parsed, error: `Duplicate: ${result.data.duplicate.name} (${result.data.duplicate.email})`, candidate_id: result.data.duplicate.id } : r
          ));
          continue;
        }

        setBulkResults(prev => prev.map((r, idx) =>
          idx === i ? { ...r, status: 'creating' } : r
        ));

        const skills = Array.isArray(parsed.skills) ? parsed.skills : 
          (typeof parsed.skills === 'string' ? parsed.skills.split(',').map(s => s.trim()).filter(Boolean) : []);

        const createResult = await createCandidate({
          name: parsed.name || 'Unknown',
          email: parsed.email || `unknown-${Date.now()}@placeholder.com`,
          phone: parsed.phone || null,
          location: parsed.location || null,
          current_company: parsed.current_company || null,
          years_of_experience: parsed.years_of_experience || null,
          skills: skills,
          tags: [],
          job_id: jobId,
          resume_file_id: resumeFileId,
          resume_filename: resumeFilename,
          source: 'Bulk Upload'
        });

        if (createResult.success) {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'done', parsed_data: parsed, candidate_id: createResult.data?.id } : r
          ));
        } else {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'error', parsed_data: parsed, error: createResult.error || 'Failed to create candidate' } : r
          ));
        }
      } catch (err) {
        setBulkResults(prev => prev.map((r, idx) =>
          idx === i ? { ...r, status: 'error', error: err.message || 'Unexpected error' } : r
        ));
      }
    }

    setBulkProcessing(false);
    loadJobData();
    e.target.value = '';
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormLoading(true);
    const result = await parseResume(file, jobId);
    setFormLoading(false);

    if (result.success) {
      const parsed = result.data.parsed_data;
      setParsedData(parsed);
      
      // Auto-fill form with parsed data
      setCandidateForm(prev => ({
        ...prev,
        name: parsed.name || prev.name,
        email: parsed.email || prev.email,
        phone: parsed.phone || prev.phone,
        location: parsed.location || prev.location,
        current_company: parsed.current_company || prev.current_company,
        years_of_experience: parsed.years_of_experience?.toString() || prev.years_of_experience,
        skills: parsed.skills || prev.skills,
        resume_file_id: result.data.resume_file_id || '',
        resume_filename: result.data.resume_filename || ''
      }));

      if (result.data.duplicate) {
        toast.warning(`Duplicate found: ${result.data.duplicate.name} (${result.data.duplicate.email})`);
      } else {
        toast.success('Resume parsed successfully');
      }
    } else {
      toast.error(result.error || 'Failed to parse resume');
    }

    // Reset file input
    if (resumeInputRef.current) {
      resumeInputRef.current.value = '';
    }
  };

  const getCandidatesByStage = (stage) => {
    return jobCandidates.filter(c => c.current_stage === stage);
  };

  const handleDragStart = (e, candidate) => {
    setDraggedCandidate(candidate);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedCandidate || draggedCandidate.current_stage === targetStage) {
      setDraggedCandidate(null);
      return;
    }

    const result = await updateCandidateStage(
      draggedCandidate.id,
      targetStage,
      `Moved from ${draggedCandidate.current_stage} to ${targetStage}`
    );

    if (result.success) {
      toast.success(`Moved ${draggedCandidate.name} to ${targetStage}`);
      loadJobData();
    } else {
      toast.error(result.error);
    }
    setDraggedCandidate(null);
  };

  const getStageColor = (idx) => {
    const colors = [
      'border-t-blue-500',
      'border-t-cyan-500',
      'border-t-purple-500',
      'border-t-amber-500',
      'border-t-emerald-500',
      'border-t-rose-500'
    ];
    return colors[idx % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-slate-400 mb-4" />
        <p className="text-slate-500">Job not found</p>
        <Button variant="outline" onClick={onBack} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{job.title}</h1>
              <Badge className={STATUS_COLORS[job.status]}>{job.status}</Badge>
            </div>
            <div className="flex items-center gap-4 text-slate-500 mt-1">
              {job.department && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" /> {job.department}
                </span>
              )}
              {job.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {job.location}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <CopyLinkButton params={{ tab: 'jobs', job_id: job.id }} what="job" />
          <Button variant="outline" onClick={() => setShowEditDialog(true)}>
            <Edit className="w-4 h-4 mr-2" /> Edit Job
          </Button>
          <Button variant="outline" onClick={() => bulkFileInputRef.current?.click()}>
            <Upload className="w-4 h-4 mr-2" /> Bulk Upload CVs
          </Button>
          <input
            ref={bulkFileInputRef}
            type="file"
            accept=".pdf"
            multiple
            className="hidden"
            onChange={handleBulkUpload}
            data-testid="bulk-upload-input-job"
          />
          <Button onClick={() => setShowAddCandidateDialog(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" /> Add Candidate
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={changeTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline ({jobCandidates.length})</TabsTrigger>
          <TabsTrigger value="candidates">Candidates</TabsTrigger>
          <TabsTrigger value="interviews">Interviews ({jobInterviews.length})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Job Details */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span>{job.employment_type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span>{job.candidate_count || jobCandidates.length} Candidates</span>
                  </div>
                  {(job.experience_min !== null || job.experience_max !== null) && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span>{job.experience_min ?? 0} - {job.experience_max ?? '∞'} years experience</span>
                    </div>
                  )}
                  {(job.salary_min || job.salary_max) && (
                    <div className="flex items-center gap-2">
                      <IndianRupee className="w-4 h-4 text-slate-400" />
                      <span>₹{job.salary_min ?? '0'} - {job.salary_max ?? '∞'} LPA</span>
                    </div>
                  )}
                </div>

                {job.description && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-slate-500">Description</Label>
                      <p className="mt-2 text-sm whitespace-pre-wrap">{job.description}</p>
                    </div>
                  </>
                )}

                {job.requirements && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-slate-500">Requirements</Label>
                      <p className="mt-2 text-sm whitespace-pre-wrap">{job.requirements}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Total Candidates</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-semibold text-purple-600">{jobCandidates.length}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Interviews Scheduled</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-semibold text-blue-600">
                    {jobInterviews.filter(i => i.status === 'Scheduled').length}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">By Stage</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {stages.map((stage, idx) => (
                    <div key={stage} className="flex justify-between text-sm">
                      <span className="text-slate-600">{stage}</span>
                      <Badge variant="outline">{getCandidatesByStage(stage).length}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline">
          <div className="flex gap-4 overflow-x-auto pb-4">
            {stages.map((stage, idx) => {
              const stageCandidates = getCandidatesByStage(stage);
              return (
                <div
                  key={stage}
                  className="flex-shrink-0 w-[280px]"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage)}
                >
                  <Card className={`border-t-4 ${getStageColor(idx)}`}>
                    <CardHeader className="py-3 px-4">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-sm font-medium">{stage}</CardTitle>
                        <Badge variant="secondary">{stageCandidates.length}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-2">
                      <ScrollArea className="h-[calc(100vh-400px)]">
                        <div className="space-y-2 p-1">
                          {stageCandidates.length === 0 ? (
                            <div className="text-center py-6 text-slate-400 text-sm">
                              No candidates
                            </div>
                          ) : (
                            stageCandidates.map((candidate) => (
                              <div
                                key={candidate.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, candidate)}
                                className={`p-3 bg-white border rounded-lg cursor-grab hover:shadow-md transition-shadow ${
                                  draggedCandidate?.id === candidate.id ? 'opacity-50' : ''
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <GripVertical className="w-4 h-4 text-slate-300 mt-1" />
                                  <div className="flex-1 min-w-0">
                                    <p 
                                      className="font-medium text-sm truncate cursor-pointer hover:text-purple-600"
                                      onClick={() => onViewCandidate(candidate.id)}
                                    >
                                      {candidate.name}
                                    </p>
                                    {candidate.current_company && (
                                      <p className="text-xs text-slate-500 truncate">{candidate.current_company}</p>
                                    )}
                                    {candidate.years_of_experience !== null && (
                                      <p className="text-xs text-slate-400">{candidate.years_of_experience} yrs</p>
                                    )}
                                  </div>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0"
                                    onClick={() => onViewCandidate(candidate.id)}
                                  >
                                    <Eye className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Candidates Tab */}
        <TabsContent value="candidates">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>All Candidates</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => bulkFileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" /> Bulk Upload
                  </Button>
                  <Button size="sm" onClick={() => setShowAddCandidateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Experience</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobCandidates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No candidates for this job
                      </TableCell>
                    </TableRow>
                  ) : (
                    jobCandidates.map(candidate => (
                      <TableRow key={candidate.id}>
                        <TableCell className="font-medium">{candidate.name}</TableCell>
                        <TableCell>{candidate.email}</TableCell>
                        <TableCell>{candidate.current_stage}</TableCell>
                        <TableCell>
                          <Badge className={candidateStatusColors[candidate.status]}>
                            {candidate.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {candidate.years_of_experience ? `${candidate.years_of_experience} yrs` : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => onViewCandidate(candidate.id)}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Interviews Tab */}
        <TabsContent value="interviews">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Interviews</CardTitle>
            </CardHeader>
            <CardContent>
              {jobInterviews.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No interviews scheduled for this job</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {jobInterviews.sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)).map(interview => {
                    const candidate = jobCandidates.find(c => c.id === interview.candidate_id);
                    return (
                      <div key={interview.id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{interview.round_name}</span>
                              <Badge variant="outline">Round {interview.round_number}</Badge>
                              <Badge className={
                                interview.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                interview.status === 'Scheduled' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {interview.status}
                              </Badge>
                            </div>
                            <p 
                              className="text-sm text-purple-600 cursor-pointer hover:underline mt-1"
                              onClick={() => candidate && onViewCandidate(candidate.id)}
                            >
                              {candidate?.name || 'Unknown Candidate'}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {interview.scheduled_at ? new Date(interview.scheduled_at).toLocaleString() : 'TBD'}
                              </span>
                              {interview.interviewer_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {interview.interviewer_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Job Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Job Title *</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select value={editForm.employment_type} onValueChange={(v) => setEditForm({ ...editForm, employment_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Min Experience (years)</Label>
                <Input
                  type="number"
                  value={editForm.experience_min}
                  onChange={(e) => setEditForm({ ...editForm, experience_min: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Experience (years)</Label>
                <Input
                  type="number"
                  value={editForm.experience_max}
                  onChange={(e) => setEditForm({ ...editForm, experience_max: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Min Salary (₹ LPA)</Label>
                <Input
                  type="number"
                  value={editForm.salary_min}
                  onChange={(e) => setEditForm({ ...editForm, salary_min: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Salary (₹ LPA)</Label>
                <Input
                  type="number"
                  value={editForm.salary_max}
                  onChange={(e) => setEditForm({ ...editForm, salary_max: e.target.value })}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={4}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Requirements</Label>
                <Textarea
                  value={editForm.requirements}
                  onChange={(e) => setEditForm({ ...editForm, requirements: e.target.value })}
                  rows={4}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateJob} disabled={formLoading}>
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Candidate Dialog */}
      <Dialog open={showAddCandidateDialog} onOpenChange={(open) => { if (!open) closeAddCandidateDialog(); else setShowAddCandidateDialog(true); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Candidate to {job.title}</DialogTitle>
            <DialogDescription>Add a new candidate or link an existing one</DialogDescription>
          </DialogHeader>
          
          {/* Mode Toggle */}
          <div className="flex gap-2 border-b pb-4">
            <Button
              variant={addCandidateMode === 'new' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAddCandidateMode('new')}
              className={addCandidateMode === 'new' ? 'bg-purple-600' : ''}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Candidate
            </Button>
            <Button
              variant={addCandidateMode === 'existing' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAddCandidateMode('existing')}
              className={addCandidateMode === 'existing' ? 'bg-purple-600' : ''}
            >
              <User className="w-4 h-4 mr-2" />
              Link Existing
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto py-4">
            {addCandidateMode === 'existing' ? (
              /* Link Existing Candidate - Searchable List */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Search Candidate by Name or Email</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={candidateSearchQuery}
                      onChange={(e) => setCandidateSearchQuery(e.target.value)}
                      placeholder="Type to search candidates..."
                      className="pl-10"
                    />
                  </div>
                </div>
                
                {/* Search Results */}
                <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                  {loadingSearchCandidates ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-8 h-8 mx-auto mb-2 text-purple-500 animate-spin" />
                      <p className="text-slate-500">Loading candidates...</p>
                    </div>
                  ) : candidateSearchQuery.trim().length < 2 ? (
                    <div className="p-8 text-center text-slate-500">
                      <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>Enter at least 2 characters to search</p>
                      <p className="text-xs mt-2">({allCandidatesForSearch.length} candidates available)</p>
                    </div>
                  ) : (
                    (() => {
                      const searchLower = candidateSearchQuery.toLowerCase();
                      
                      // Filter: show candidates NOT already in this job
                      const filteredCandidates = allCandidatesForSearch.filter(c => {
                        const notInThisJob = !c.job_id || c.job_id !== jobId;
                        const matchesSearch = c.name?.toLowerCase().includes(searchLower) ||
                                             c.email?.toLowerCase().includes(searchLower);
                        return notInThisJob && matchesSearch;
                      });
                      
                      if (filteredCandidates.length === 0) {
                        return (
                          <div className="p-8 text-center text-slate-500">
                            <User className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                            <p>No candidates found matching "{candidateSearchQuery}"</p>
                            <p className="text-xs mt-2">Searched in {allCandidatesForSearch.length} candidates</p>
                          </div>
                        );
                      }
                      
                      return filteredCandidates.map(candidate => (
                        <div
                          key={candidate.id}
                          className="p-3 border-b last:border-b-0 hover:bg-purple-50 cursor-pointer transition-colors"
                          onClick={() => handleLinkExistingCandidate(candidate.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{candidate.name}</p>
                              <p className="text-sm text-slate-500">{candidate.email}</p>
                              {candidate.current_company && (
                                <p className="text-xs text-slate-400">{candidate.current_company}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {candidate.job_id && candidate.job_id !== jobId ? (
                                <Badge variant="outline" className="text-xs">
                                  In another job
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                  Available
                                </Badge>
                              )}
                              <Button size="sm" variant="ghost" className="text-purple-600" disabled={formLoading}>
                                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Link'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ));
                    })()
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Click on a candidate to link them to this job position
                </p>
              </div>
            ) : (
              /* Add New Candidate Form */
              <div className="space-y-4">
                {/* Resume Upload */}
                <div className="space-y-2 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <Label className="text-purple-700 font-medium">AI Resume Parser</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => resumeInputRef.current?.click()}
                      disabled={formLoading}
                      className="gap-2"
                    >
                      {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Upload Resume (PDF)
                    </Button>
                    <input
                      ref={resumeInputRef}
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={handleResumeUpload}
                    />
                  </div>
                  {parsedData && (
                    <div className="text-xs text-purple-600 space-y-1">
                      <p className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Resume parsed - fields auto-filled
                      </p>
                      {candidateForm.resume_filename && (
                        <p className="flex items-center gap-1 text-green-600">
                          <FileText className="w-3 h-3" />
                          Resume attached: {candidateForm.resume_filename}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={candidateForm.name}
                      onChange={(e) => setCandidateForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={candidateForm.email}
                      onChange={(e) => setCandidateForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={candidateForm.phone}
                      onChange={(e) => setCandidateForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 9999999999"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location / City</Label>
                    <Input
                      value={candidateForm.location}
                      onChange={(e) => setCandidateForm(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g. Mumbai, Bangalore"
                      data-testid="job-candidate-location"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Current Company</Label>
                    <Input
                      value={candidateForm.current_company}
                      onChange={(e) => setCandidateForm(prev => ({ ...prev, current_company: e.target.value }))}
                      placeholder="Company name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Experience (Years)</Label>
                    <Input
                      type="number"
                      value={candidateForm.years_of_experience}
                      onChange={(e) => setCandidateForm(prev => ({ ...prev, years_of_experience: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notice Period</Label>
                    <Select 
                      value={candidateForm.notice_period || 'not_specified'} 
                      onValueChange={(v) => setCandidateForm(prev => ({ ...prev, notice_period: v === 'not_specified' ? '' : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_specified">Not specified</SelectItem>
                        <SelectItem value="Immediate">Immediate</SelectItem>
                        <SelectItem value="15 days">15 days</SelectItem>
                        <SelectItem value="30 days">30 days</SelectItem>
                        <SelectItem value="60 days">60 days</SelectItem>
                        <SelectItem value="90 days">90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Current CTC (LPA)</Label>
                    <Input
                      type="number"
                      value={candidateForm.current_ctc}
                      onChange={(e) => setCandidateForm(prev => ({ ...prev, current_ctc: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expected CTC (LPA)</Label>
                    <Input
                      type="number"
                      value={candidateForm.expected_ctc}
                      onChange={(e) => setCandidateForm(prev => ({ ...prev, expected_ctc: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select 
                    value={candidateForm.source || 'Direct'} 
                    onValueChange={(v) => setCandidateForm(prev => ({ ...prev, source: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Direct">Direct</SelectItem>
                      <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                      <SelectItem value="Naukri">Naukri</SelectItem>
                      <SelectItem value="Referral">Referral</SelectItem>
                      <SelectItem value="Campus">Campus</SelectItem>
                      <SelectItem value="Agency">Agency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Skills */}
                <div className="space-y-2">
                  <Label>Skills</Label>
                  <div className="flex gap-2">
                    <Input
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      placeholder="Add a skill..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    />
                    <Button type="button" variant="outline" onClick={addSkill}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {candidateForm.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {candidateForm.skills.map((skill, idx) => (
                        <Badge key={idx} variant="secondary" className="gap-1">
                          {skill}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => removeSkill(skill)} />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tags with Autocomplete */}
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="relative">
                    <div className="flex gap-2">
                      <Input
                        value={tagInput}
                        onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
                        placeholder="Add a tag..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        onFocus={() => setShowTagSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                        data-testid="job-tag-input"
                      />
                      <Button type="button" variant="outline" onClick={() => addTag()}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {showTagSuggestions && filteredTagSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-32 overflow-y-auto">
                        {filteredTagSuggestions.map((tag, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-1.5 text-sm cursor-pointer hover:bg-purple-50 hover:text-purple-700"
                            onMouseDown={(e) => { e.preventDefault(); addTag(tag); }}
                          >
                            {tag}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {candidateForm.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {candidateForm.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="gap-1 bg-purple-50 text-purple-700 border-purple-200">
                          <Tag className="w-3 h-3" />
                          {tag}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={candidateForm.notes}
                    onChange={(e) => setCandidateForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={closeAddCandidateDialog}>Cancel</Button>
            {addCandidateMode === 'new' && (
              <Button onClick={handleAddCandidate} disabled={formLoading} className="bg-purple-600 hover:bg-purple-700">
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Candidate
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Results Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { if (!bulkProcessing) setUploadDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-upload-dialog-job">
          <DialogHeader>
            <DialogTitle>Bulk Upload Results — {job?.title || 'Job'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {bulkResults.map((result, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${
                result.status === 'done' ? 'bg-green-50 border-green-200' :
                result.status === 'error' ? 'bg-red-50 border-red-200' :
                result.status === 'duplicate' ? 'bg-yellow-50 border-yellow-200' :
                result.status === 'uploading' || result.status === 'creating' ? 'bg-blue-50 border-blue-200' :
                'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium">{result.filename}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.status === 'pending' && <Badge variant="outline">Waiting</Badge>}
                    {result.status === 'uploading' && <Badge className="bg-blue-100 text-blue-700"><Loader2 className="w-3 h-3 animate-spin mr-1" />Parsing</Badge>}
                    {result.status === 'creating' && <Badge className="bg-blue-100 text-blue-700"><Loader2 className="w-3 h-3 animate-spin mr-1" />Creating</Badge>}
                    {result.status === 'done' && <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Created</Badge>}
                    {result.status === 'error' && <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Failed</Badge>}
                    {result.status === 'duplicate' && <Badge className="bg-yellow-100 text-yellow-700"><AlertCircle className="w-3 h-3 mr-1" />Duplicate</Badge>}
                    {(result.status === 'done' || result.status === 'duplicate') && result.candidate_id && (
                      <Button size="sm" variant="ghost" className="text-purple-600 h-6 px-2" onClick={() => openCandidateNewWindow(result.candidate_id)}>
                        <ExternalLink className="w-3 h-3 mr-1" />Open
                      </Button>
                    )}
                  </div>
                </div>
                {result.parsed_data && result.status === 'done' && (
                  <div className="mt-1">
                    <p className="text-sm text-green-700">
                      {result.parsed_data.name}{result.parsed_data.email ? ` — ${result.parsed_data.email}` : ''}
                      {result.parsed_data.location ? ` — ${result.parsed_data.location}` : ''}
                    </p>
                    {result.parsed_data.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="text-[10px] text-slate-500 mr-1">Skills:</span>
                        {(Array.isArray(result.parsed_data.skills) ? result.parsed_data.skills : []).slice(0, 5).map((skill, si) => (
                          <Badge key={si} variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">{skill}</Badge>
                        ))}
                        {result.parsed_data.skills.length > 5 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">+{result.parsed_data.skills.length - 5} more</Badge>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {result.error && (
                  <p className="text-sm text-red-600 mt-1">{result.error}</p>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setUploadDialogOpen(false)} disabled={bulkProcessing}>
              {bulkProcessing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processing...</> : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetail;
